import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useQuestionnaireState, useRecommendationStatus } from '../hooks/useApi';
import { storage } from '../utils/utils';
import { CompletionLoading } from '../components/Loading';

export const Route = createFileRoute('/results')({
  validateSearch: search => ({ sessionId: search.sessionId }),
  component: ResultsPage,
});

/**
 * Processing status component with CompletionLoading and background status checking
 */
function ProcessingStatus({ sessionId, userEmail, onComplete, onError, showNotification }) {
  const [timeElapsed, setTimeElapsed] = useState(0);
  const statusQuery = useRecommendationStatus(sessionId);

  // Timer for elapsed time
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeElapsed(prev => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Handle status changes
  useEffect(() => {
    if (statusQuery.data) {
      if (statusQuery.data.completed) {
        showNotification('Recommendations sent to your email!', 'success');
        onComplete();
      }
    }

    if (statusQuery.error) {
      onError('Failed to process recommendations. Please try again.');
    }
  }, [statusQuery.data, statusQuery.error, showNotification, onComplete, onError]);

  // Handle timeout from CompletionLoading
  const handleTimeout = () => {
    console.log('⏰ Processing taking longer than expected, but continuing in background');
    showNotification(
      'Processing is taking longer than usual, but continues in background. Check your email in a few minutes.',
      'warning',
    );
  };

  return (
    <CompletionLoading
      onTimeout={handleTimeout}
      maxDuration={300} // 5 minutes before showing timeout message
    />
  );
}

/**
 * Email success screen - Nordic minimal design
 */
function EmailSuccess({ userEmail, onStartOver }) {
  return (
    <div className='bg-white rounded-2xl p-8 shadow-xl'>
      <div className='max-w-md mx-auto'>
        {/* Nordic Typography */}
        <div className='text-sm text-gray-500 mb-2 uppercase tracking-wide text-center'>
          Success
        </div>
        <h2 className='text-2xl font-light text-gray-900 mb-2 text-center'>
          Recommendations Sent
        </h2>
        <p className='text-sm text-gray-600 mb-8 text-center'>
          Your personalized recommendations have been sent to your email
        </p>

        {/* Email confirmation - Minimal */}
        <div className='bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6'>
          <div className='text-xs text-gray-500 uppercase tracking-wider mb-2'>Email Sent To</div>
          <div className='font-mono text-sm text-gray-900 mb-4'>{userEmail}</div>
          
          <div className='text-xs text-gray-600 space-y-2'>
            <div>• Check inbox and spam folder</div>
            <div>• Arrives within 2-3 minutes</div>
            <div>• Add sender to contacts</div>
          </div>
        </div>

        {/* What's included - Minimal */}
        <div className='bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6'>
          <div className='text-xs text-gray-500 uppercase tracking-wider mb-3'>What's Included</div>
          <div className='text-sm text-gray-600 space-y-2'>
            <div>• Personalized card recommendations</div>
            <div>• Fees, benefits, and rewards</div>
            <div>• Direct application links</div>
            <div>• Detailed card analysis</div>
          </div>
        </div>

        {/* Action button - Dark Nordic style */}
        <button
          onClick={onStartOver}
          className='w-full py-3 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors'
        >
          Start New Session
        </button>
      </div>
    </div>
  );
}

const states = {
  error: 0,
  processing: 1,
  success: 2,
};

function ResultsPage() {
  const navigate = useNavigate();
  const context = Route.useRouteContext();
  const showNotification = context?.showNotification || (() => {});
  const { sessionId } = Route.useSearch();

  const [currentState, setCurrentState] = useState(states.processing);
  const [userEmail, setUserEmail] = useState('');

  const { clearAllData } = useQuestionnaireState();

  // Get user email from storage (optional)
  useEffect(() => {
    const email = storage.getUserEmail();
    if (email) {
      setUserEmail(email);
    }
  }, []);

  // Handle processing completion - navigate to analysis
  const handleComplete = () => {
    console.log(`🎉 Processing completed for session: ${sessionId}`);
    // Clear session data but keep email for next time
    storage.clearSession();
    // Navigate to analysis page
    navigate({ to: `/analysis/${sessionId}` });
  };

  // Handle processing error
  const handleError = message => {
    console.error(`❌ Processing error: ${message}`);
    setCurrentState(states.error);
    showNotification(message, 'error');
  };

  // Handle starting over
  const handleStartOver = () => {
    // Clear session but keep email
    storage.clearSession();
    clearAllData();
    navigate({ to: '/' });
    showNotification('Ready for a new session!', 'info');
  };

  if (currentState === states.processing) {
    return (
      <ProcessingStatus
        sessionId={sessionId || storage.getSessionId()}
        userEmail={userEmail}
        onComplete={handleComplete}
        onError={handleError}
        showNotification={showNotification}
      />
    );
  }

  if (currentState === states.success) {
    return <EmailSuccess userEmail={userEmail} onStartOver={handleStartOver} />;
  }

  if (currentState === states.error) {
    return (
      <div className='bg-white rounded-2xl p-8 shadow-xl'>
        <div className='max-w-md mx-auto text-center'>
          <div className='text-sm text-gray-500 mb-2 uppercase tracking-wide'>Error</div>
          <h2 className='text-2xl font-light text-gray-900 mb-3'>Processing Failed</h2>
          <p className='text-sm text-gray-600 mb-6'>
            We encountered an issue processing your recommendations
          </p>

          <div className='bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6 text-left'>
            <div className='text-xs text-gray-500 uppercase tracking-wider mb-2'>Need Help?</div>
            <div className='text-sm text-gray-600'>
              Contact support:
              <div className='font-mono text-sm text-gray-900 mt-2'>
                <a href='mailto:contact@ollie.ae' className='hover:underline'>
                  contact@ollie.ae
                </a>
              </div>
            </div>
          </div>

          <button
            onClick={handleStartOver}
            className='w-full py-3 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors'
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-white rounded-2xl p-8 shadow-xl'>
      <div className='flex items-center justify-center gap-3'>
        <span className='text-sm text-gray-600'>Loading...</span>
        <div className='w-4 h-4 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin'></div>
      </div>
    </div>
  );
}
